import { type Plugin } from "@nuxt/types";

import { isAuth0UserToken } from "@/schemes/auth0";
import { FORBIDDEN, UNAUTHORIZED } from "@/utils/enums/http-codes";
import { privacyServiceBaseUrl } from "@/utils/services/PrivacyService";

/**
 * @nuxtjs/auth and @nuxtjs/axios plugin.
 *
 * Refreshes user on network errors. Sets hidden internal user data.
 */
const plugin: Plugin = ({ $axios, $auth, $config }) => {
  // Handle Axios Auth errors
  $axios.onResponseError(async (error) => {
    // An unexpected error type
    if (typeof error !== "object" || error === null) {
      throw error;
    }

    // Validate that error is an axios error type
    if ("response" in error === false && "config" in error === false) {
      throw error;
    }

    if (error.config?.url?.endsWith(privacyServiceBaseUrl)) {
      // Ignore errors on the privacy service URL
      throw error;
    }

    if (error.response?.status === UNAUTHORIZED) {
      $auth.logout();
    } else if (
      error.response?.status === FORBIDDEN &&
      typeof error.response?.data === "object" &&
      error.response?.data !== null &&
      "error" in error.response.data &&
      (error.response.data as { error?: string }).error === "Key not authorised"
    ) {
      await $auth.refreshTokens();
    }

    throw error;
  });

  // Set a sigil ID header so that server private requests can succeed
  if ($config.isServer === true && $auth.loggedIn && $auth.user !== null) {
    const userId = isAuth0UserToken($auth.user)
      ? $auth.user["http://shopltk.com/profile"].sigilUserId
      : $auth.user.userId;

    $axios.setHeader("X-SIGIL-ID", userId);
  }
};

export default plugin;
